import { useState, useEffect, useCallback } from "react";
import { useDependency } from "../../contexts/DependencyProvider";
import { ListMailboxRequest } from "../../services/soap/mail/requests/ListMailboxRequest";
import { GetMailboxRequest } from "../../services/soap/mail/requests/GetMailboxRequest";
import { ListMessageTypeRequest } from "../../services/soap/message/requests/ListMessageTypeRequest";
import moment from "moment";
import { NewMessageFormData } from "./useNewMessageForm";

/**
 * Supported message types
 * 1 = Note, 6 = Phone, 7 = Email, 12 = Meeting
 */
export const SUPPORTED_MESSAGE_TYPES = ["1", "6", "7", "12"] as const;
export type SupportedMessageType = (typeof SUPPORTED_MESSAGE_TYPES)[number];

/**
 * Mailbox option interface
 */
export interface MailboxOption {
  id: string;
  name: string;
}

/**
 * Message type option interface
 */
export interface MessageTypeOption {
  id: string;
  text: string;
  type: string;
  priority: string;
}

/**
 * Communication settings returned by the hook
 */
export interface CommunicationSettings {
  mailboxes: MailboxOption[];
  messageTypes: MessageTypeOption[];
  defaultMailbox: MailboxOption | null;
  mailboxSignature: string;
  defaultMessageType: MessageTypeOption | null;
}

interface UseNewMessageSettingsOptions {
  /** Session object containing user preferences */
  session: any;
  /** Optional message object for reply scenarios */
  messageObj?: any;
  /** Reply type: 'REPLY' | 'REPLYALL' | undefined */
  replyType?: string;
  /** Callback when settings are loaded */
  onSettingsLoaded?: (
    settings: CommunicationSettings,
    defaultFormFields: Partial<NewMessageFormData>
  ) => void;
  /** Callback on error */
  onError?: (error: any) => void;
}

/**
 * Format signature text for HTML display
 */
export const formatSignature = (signature: string): string => {
  return signature ? "<br/>" + signature.replace(/\n/g, "<br/>") : "";
};

/**
 * Parse email strings into structured objects
 */
export const parseEmails = (emailString: string | string[]): any[] => {
  if (!emailString) return [];

  const emails = Array.isArray(emailString)
    ? emailString
    : emailString.split(",");

  return emails
    .map((email: string) => ({
      id: email.trim(),
      name: email.trim(),
      contactName: email.trim(),
      emailText: email.trim(),
    }))
    .filter((e: any) => e.id);
};

/**
 * Escape HTML special characters
 */
export const escapeHtml = (str: string): string => {
  return (
    str?.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;") ||
    ""
  );
};

/**
 * Parse recipients from message object (handles various structures)
 */
export const parseRecipients = (
  objMessage: any
): { toEmails: any[]; ccEmails: any[] } => {
  const toEmails: any[] = [];
  const ccEmails: any[] = [];

  if (!objMessage?.RECIPIENT) return { toEmails, ccEmails };

  if (Array.isArray(objMessage.RECIPIENT)) {
    toEmails.push(...parseEmails(objMessage.RECIPIENT));
  } else if (objMessage.RECIPIENT.TO?.CONTENT) {
    toEmails.push(...parseEmails(objMessage.RECIPIENT.TO.CONTENT));
    if (objMessage.RECIPIENT.CC?.CONTENT) {
      ccEmails.push(...parseEmails(objMessage.RECIPIENT.CC.CONTENT));
    }
  } else if (typeof objMessage.RECIPIENT === "string") {
    toEmails.push(...parseEmails(objMessage.RECIPIENT));
  }

  return { toEmails, ccEmails };
};

/**
 * Build reply content with original message header
 */
export const buildReplyContent = (
  objMessage: any,
  toEmails: any[],
  ccEmails: any[],
  signature: string
): { subject: string; content: string } => {
  const originalSubject =
    objMessage.subject || objMessage.SUBJECT?.string || "";
  const replySubject = originalSubject.toLowerCase().startsWith("re:")
    ? originalSubject
    : `Re: ${originalSubject}`;

  // Build original message header
  const contentDetail =
    `<b>From:</b> ${escapeHtml(
      objMessage.mailFrom || objMessage.sender || ""
    )}<br/>` +
    `<b>Sent:</b> ${
      objMessage.date
        ? moment(objMessage.date).format("DD MMMM YYYY HH:mm")
        : ""
    }<br/>` +
    `<b>To:</b> ${escapeHtml(
      toEmails.map((e: any) => e.emailText).join(", ")
    )}<br/>` +
    (ccEmails.length > 0
      ? `<b>Cc:</b> ${escapeHtml(
          ccEmails.map((e: any) => e.emailText).join(", ")
        )}<br/>`
      : "") +
    `<b>Subject:</b> ${escapeHtml(originalSubject)}<br/><br/>`;

  const originalContent =
    contentDetail +
    (objMessage.PREVIEW?.content ||
      objMessage.BODY?.string ||
      objMessage.teaser?.replace(/\n/g, "<br/>") ||
      "");

  const replyContent = `${signature}<br/><br/>-----Original Message-----<br/><br/>${originalContent}`;

  return { subject: replySubject, content: replyContent };
};

/**
 * Hook for loading and managing new message communication settings
 * Handles mailbox loading, message types, and reply template population
 */
export const useNewMessageSettings = ({
  session,
  messageObj,
  replyType,
  onSettingsLoaded,
  onError,
}: UseNewMessageSettingsOptions) => {
  const { store, mailService, messageService } = useDependency();

  const [isLoading, setIsLoading] = useState(false);
  const [mailboxes, setMailboxes] = useState<MailboxOption[]>([]);
  const [messageTypes, setMessageTypes] = useState<MessageTypeOption[]>([]);
  const [defaultMailbox, setDefaultMailbox] = useState<MailboxOption | null>(
    null
  );
  const [mailboxSignature, setMailboxSignature] = useState("");
  const [defaultMessageType, setDefaultMessageType] =
    useState<MessageTypeOption | null>(null);
  const [error, setError] = useState<any>(null);

  /**
   * Fetch mailbox details and signature
   */
  const fetchMailboxDetails = useCallback(
    async (mailboxId: number): Promise<{ mailbox: any; signature: string }> => {
      try {
        const response = await mailService.getMailbox(
          new GetMailboxRequest(store.Server, store.SessionId, mailboxId)
        );

        const signature = response?.MAILBOX?.signature
          ? formatSignature(response.MAILBOX.signature)
          : "";

        return { mailbox: response?.MAILBOX, signature };
      } catch (err) {
        console.error("Error fetching mailbox details:", err);
        return { mailbox: null, signature: "" };
      }
    },
    [mailService, store.Server, store.SessionId]
  );

  /**
   * Main effect to load communication settings
   */
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setIsLoading(true);
        setError(null);

        // Parallel fetch for better performance
        const [mailboxResponse, messageTypesResponse] = await Promise.all([
          mailService.listMailbox(
            new ListMailboxRequest(store.Server, store.SessionId, {
              usableByUser: store.UserId,
              offset: 0,
              limit: 100,
            })
          ),
          messageService.listMessageType(
            new ListMessageTypeRequest(store.Server, store.SessionId)
          ),
        ]);

        // Process mailboxes
        let availableMailboxes: MailboxOption[] = [];
        let defaultMb: MailboxOption | null = null;
        let signature = "";

        if (
          mailboxResponse?.result === "OK" &&
          mailboxResponse.MAILBOX?.length > 0
        ) {
          availableMailboxes = mailboxResponse.MAILBOX.map((mailbox: any) => ({
            id: mailbox.id,
            name: `${mailbox.name}@${mailbox.domainName}`,
          }));

          // Find default mailbox based on user preferences
          defaultMb =
            availableMailboxes.find(
              (mb) => mb.id === session?.userPreferences?.mailboxId
            ) ||
            availableMailboxes[0] ||
            null;

          if (defaultMb) {
            const details = await fetchMailboxDetails(Number(defaultMb.id));
            signature = details.signature;
          }
        }

        // Process message types
        let sortedMessageTypes: MessageTypeOption[] = [];
        let defaultMsgType: MessageTypeOption | null = null;

        if (
          messageTypesResponse?.result === "OK" &&
          messageTypesResponse.ITEM
        ) {
          sortedMessageTypes = messageTypesResponse.ITEM.filter((item: any) =>
            SUPPORTED_MESSAGE_TYPES.includes(item.type)
          )
            .sort(
              (a: any, b: any) =>
                (Number(b.priority) || 0) - (Number(a.priority) || 0)
            )
            .map((item: any) => ({
              id: item.id,
              text: item.name,
              type: item.type,
              priority: item.priority,
            }));

          defaultMsgType = sortedMessageTypes[0] || null;
        }

        // Update local state
        setMailboxes(availableMailboxes);
        setMessageTypes(sortedMessageTypes);
        setDefaultMailbox(defaultMb);
        setMailboxSignature(signature);
        setDefaultMessageType(defaultMsgType);

        // Build default form fields
        const defaultFormFields: Partial<NewMessageFormData> = {
          from: defaultMb?.name || "",
          fromValue: defaultMb,
          mailBox: signature,
          emailContent: signature,
          messageType: defaultMsgType?.type || "",
          messageTypeValue: defaultMsgType
            ? { text: defaultMsgType.text, type: defaultMsgType.type }
            : null,
        };

        // Notify parent component
        if (onSettingsLoaded) {
          const settings: CommunicationSettings = {
            mailboxes: availableMailboxes,
            messageTypes: sortedMessageTypes,
            defaultMailbox: defaultMb,
            mailboxSignature: signature,
            defaultMessageType: defaultMsgType,
          };
          onSettingsLoaded(settings, defaultFormFields);
        }
      } catch (err) {
        console.error("Error loading communication settings:", err);
        setError(err);
        onError?.(err);
      } finally {
        setIsLoading(false);
      }
    };

    loadSettings();
  }, [
    store.Server,
    store.SessionId,
    store.UserId,
    session?.userPreferences?.mailboxId,
  ]);

  /**
   * Populate reply template when message object and mailbox signature are available
   */
  const populateReplyTemplate =
    useCallback((): Partial<NewMessageFormData> | null => {
      if (!replyType || !messageObj || !mailboxSignature) return null;

      try {
        const objMessage = messageObj.MESSAGE || messageObj;
        const { toEmails, ccEmails } = parseRecipients(objMessage);

        // Parse notify users
        const notifyUsers = Array.isArray(objMessage.RECIPIENT?.USER)
          ? objMessage.RECIPIENT.USER.map((user: any) => ({
              id: user.id,
              contactName: user.contact,
            })).filter((e: any) => e.id)
          : [];

        // Build reply content
        const { subject: replySubject, content: replyContent } =
          buildReplyContent(objMessage, toEmails, ccEmails, mailboxSignature);

        // Parse sender email for reply
        const senderEmail = parseEmails(objMessage.mailFrom);
        const isReplyAll = replyType === "REPLYALL";

        return {
          to: senderEmail,
          cc: isReplyAll ? [...toEmails, ...ccEmails] : [],
          notify: notifyUsers,
          subject: replySubject,
          emailContent: replyContent,
          messageType: "7", // Email type
          messageTypeValue: { text: "Email", type: "7" },
        };
      } catch (err) {
        console.error("Error populating reply template:", err);
        return null;
      }
    }, [replyType, messageObj, mailboxSignature]);

  /**
   * Change mailbox and update signature
   */
  const changeMailbox = useCallback(
    async (mailboxId: string): Promise<string> => {
      const details = await fetchMailboxDetails(Number(mailboxId));
      setMailboxSignature(details.signature);
      return details.signature;
    },
    [fetchMailboxDetails]
  );

  return {
    isLoading,
    error,
    mailboxes,
    messageTypes,
    defaultMailbox,
    mailboxSignature,
    defaultMessageType,
    populateReplyTemplate,
    changeMailbox,
  };
};
